import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useFeatureAccess, FeatureKey } from '../hooks/useFeatureAccess';

interface ProtectedFeatureProps {
  feature: FeatureKey;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Protects a feature based on permission checks.
 * Shows access denied message if user doesn't have the required permission.
 */
export default function ProtectedFeature({
  feature,
  children,
  fallback,
}: ProtectedFeatureProps) {
  const { hasAccess, isLoading } = useFeatureAccess(feature);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress size={40} />
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          Checking access...
        </Typography>
      </Box>
    );
  }

  if (!hasAccess) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          bgcolor: 'background.default',
          p: 3,
        }}
      >
        <Typography variant="h5" color="error" gutterBottom>
          Access Denied
        </Typography>
        <Typography color="text.secondary" textAlign="center">
          You don't have permission to access this feature.
          <br />
          Please contact your administrator to request access.
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
}
